import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Search } from "lucide-react";
import { search as searchApi } from "@/lib/api";
import { getFlipConfig } from "@/lib/flipApi";
import { entityLabel, entityName, entityPath, fmtUSD } from "@/lib/format";
import WalletMenu from "@/components/WalletMenu";
import { useConfig } from "@/context/ConfigContext";
import { isRobinhoodEcosystemEnabled } from "@/lib/robinhoodFlags";
import { isSolanaEcosystemEnabled } from "@/lib/solanaFlags";
import { isBaseEcosystemEnabled } from "@/lib/baseFlags";


function SearchBox() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const onChange = async (e) => {
    const val = e.target.value;
    setQ(val);
    if (val.trim().length < 1) {
      setResults([]);
      setOpen(false);
      return;
    }
    try {
      const r = await searchApi(val);
      setResults(r);
      setOpen(true);
    } catch (err) {}
  };

  const go = (e) => {
    navigate(entityPath(e));
    setQ("");
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="relative w-full">
      <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2">
        <Search className="h-4 w-4 shrink-0 text-zinc-500" strokeWidth={1.5} />
        <input
          data-testid="search-input"
          value={q}
          onChange={onChange}
          onFocus={() => q && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder="Search token, ticker, @handle, contract"
          className="w-full bg-transparent text-sm outline-none placeholder:text-zinc-600"
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-white/10 bg-[#0a0a0a] shadow-2xl">
          {results.map((e) => (
            <button
              key={e.id}
              data-testid={`search-result-${e.slug}`}
              onMouseDown={() => go(e)}
              className="flex w-full items-center justify-between px-3 py-2.5 text-left transition-colors hover:bg-white/5"
            >
              <div className="flex items-center gap-2">
                <span className="font-display font-bold">{entityLabel(e)}</span>
                <span className="text-xs text-zinc-500">{entityName(e)}</span>
              </div>
              <span className="font-mono text-xs text-zinc-400">{fmtUSD(e.total_backed_usd)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function navLinkClass(active, extra = "") {
  // Transparent border always reserved so focus/active never shifts the search bar.
  return [
    "shrink-0 whitespace-nowrap rounded-full border border-transparent px-2.5 py-1.5",
    "text-sm font-bold uppercase tracking-wide transition-colors sm:px-3",
    "outline-none focus-visible:border-white/25 focus-visible:bg-white/10",
    active ? "bg-white/10 text-white" : "text-zinc-500 hover:text-white",
    extra,
  ].filter(Boolean).join(" ");
}

export default function Navbar({ category }) {
  const location = useLocation();
  const config = useConfig();
  const isTokens = category ? category === "tokens" : location.pathname.startsWith("/token") || location.pathname === "/";
  const [showSearch, setShowSearch] = useState(false);
  const [flipEnabled, setFlipEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getFlipConfig()
      .then((cfg) => {
        if (!cancelled) setFlipEnabled(Boolean(cfg?.enabled));
      })
      .catch(() => {
        if (!cancelled) setFlipEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <header className="pt-safe sticky top-0 z-40 border-b border-white/10 bg-black/70 backdrop-blur-xl">
      <div className="mx-auto flex max-w-6xl items-center gap-2 px-3 py-3 sm:gap-4 sm:px-4">
        <Link to="/" data-testid="logo-home" className="flex shrink-0 items-center gap-1.5">
          <span className="font-display text-2xl font-black tracking-tighter">FLIP</span>
          <span className="hidden text-lg text-gold sm:inline">↑</span>
        </Link>

        <nav className="no-scrollbar flex min-w-0 flex-1 items-center gap-1 overflow-x-auto" aria-label="Main">
          <Link
            to="/"
            data-testid="nav-tokens"
            className={navLinkClass(location.pathname === "/" && isTokens)}
          >
            TOKENS
          </Link>
          <Link
            to="/callers"
            data-testid="nav-callers"
            className={navLinkClass(location.pathname === "/callers")}
          >
            KOLS
          </Link>
          <Link
            to="/backers"
            data-testid="nav-backers"
            className={navLinkClass(location.pathname === "/backers", "hidden sm:inline-flex")}
          >
            TOP BACKERS
          </Link>
          <Link
            to="/battles"
            data-testid="nav-battles"
            className={navLinkClass(location.pathname.startsWith("/battle"), "hidden sm:inline-flex")}
          >
            BATTLES
          </Link>
          <Link
            to="/notifications"
            data-testid="nav-notifications"
            className={navLinkClass(location.pathname === "/notifications", "hidden md:inline-flex")}
          >
            ALERTS
          </Link>
          <Link
            to="/rewards"
            data-testid="nav-rewards"
            className={navLinkClass(location.pathname === "/rewards", "hidden md:inline-flex")}
          >
            REWARDS
          </Link>
          <Link
            to="/stats"
            data-testid="nav-stats"
            className={navLinkClass(location.pathname === "/stats")}
          >
            STATS
          </Link>
          {isRobinhoodEcosystemEnabled(config) && (
            <Link
              to="/ecosystem/robinhood"
              data-testid="nav-robinhood"
              className={navLinkClass(
                location.pathname.startsWith("/ecosystem/robinhood")
                  || location.pathname.startsWith("/chain/robinhood"),
                "hidden sm:inline-flex",
              )}
            >
              ROBINHOOD
            </Link>
          )}
          {isSolanaEcosystemEnabled(config) && (
            <Link
              to="/ecosystem/solana"
              data-testid="nav-solana"
              className={navLinkClass(
                location.pathname.startsWith("/ecosystem/solana")
                  || location.pathname.startsWith("/chain/solana"),
                "hidden sm:inline-flex",
              )}
            >
              SOLANA
            </Link>
          )}
          {isBaseEcosystemEnabled(config) && (
            <Link
              to="/ecosystem/base"
              data-testid="nav-base"
              className={navLinkClass(
                location.pathname.startsWith("/ecosystem/base")
                  || location.pathname.startsWith("/chain/base"),
                "hidden sm:inline-flex",
              )}
            >
              BASE
            </Link>
          )}
          {flipEnabled && (
            <>
              <Link
                to="/calls"
                data-testid="nav-calls"
                className={navLinkClass(location.pathname.startsWith("/calls"))}
              >
                CALLS
              </Link>
              <Link
                to="/campaigns"
                data-testid="nav-campaigns"
                className={navLinkClass(location.pathname.startsWith("/campaign"), "hidden sm:inline-flex")}
              >
                CAMPAIGNS
              </Link>
              <Link
                to="/flip"
                data-testid="nav-flip"
                className={navLinkClass(location.pathname === "/flip")}
              >
                $FLIP
              </Link>
            </>
          )}
        </nav>

        {/* Fixed-width search so nav active/focus never reflows it */}
        <div className="hidden w-64 shrink-0 md:block lg:w-72">
          <SearchBox />
        </div>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          <button
            data-testid="mobile-search-toggle"
            aria-label="Search"
            onClick={() => setShowSearch((s) => !s)}
            className="rounded-full border border-white/10 p-2 text-zinc-400 outline-none focus-visible:border-white/25 md:hidden"
          >
            <Search className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <WalletMenu />
        </div>
      </div>
      {showSearch && (
        <div className="border-t border-white/10 px-4 py-3 md:hidden">
          <SearchBox />
        </div>
      )}
    </header>
  );
}
